/**
 * Home country variants — adapta el title/description/H1/intro del home
 * (`/`) según el país detectado por el worker. Sin redirect — la URL
 * sigue siendo `/` y el canonical no cambia (el sitio es uno solo). Lo
 * que cambia es el copy que ve el visitor: a un visitor MX le hablamos
 * de "modelos mexicanas", a un US-visitor en inglés sobre "latina models".
 *
 * Por qué no redirect a /es-mx/ o /us/:
 *   - El sitio es single-canonical. Subdominios/paths localizados generan
 *     duplicate content sin ganancia real (Google entiende hreflang).
 *   - Una sola URL recibe todas las backlinks → mejor para domain authority.
 *   - Redirigir basado en geo puede romper para crawlers y para users con VPN.
 *
 * Cómo lee el país: `useTemplateSelection()` ya devuelve `country` desde
 * `__TEMPLATE_CONFIG__` que el worker inyecta. Si no hay match, cae a
 * "AR" (default por brand origin).
 *
 * Update flow: editar este archivo → deploy. No es admin-editable por
 * scope (los copy variants son producto, no ops). Si en el futuro se
 * quiere edición dinámica, replicar pattern de `country_landing_overrides`.
 */

export interface HomeVariant {
  /** ISO 3166-1 país objetivo. */
  country: string;
  /** Locale formal del variant. */
  locale: string;
  /** Title del `<title>`. ≤ 60 chars para no cortar en SERP. */
  title: string;
  /** Meta description. ≤ 160 chars. */
  description: string;
  /** Heading principal visible. */
  h1: string;
  /** Línea complementaria al H1. */
  tagline: string;
}

const BRAND_NAME = "Jua Perrone";

/**
 * Variants por país. El `default` se usa cuando el país no matchea
 * ninguno (visitors EU/Asia/UNKNOWN cuando worker no resolvió).
 */
export const HOME_COUNTRY_VARIANTS: Record<string, HomeVariant> = {
  // Argentina — brand origin, default home en español rioplatense.
  AR: {
    country: "AR",
    locale: "es-AR",
    title: `${BRAND_NAME} | Influencer y Modelo Argentina · Content Creator`,
    description: `${BRAND_NAME} — influencer argentina y creadora de contenido. Modelo, lifestyle y moda desde Buenos Aires.`,
    h1: `${BRAND_NAME}: modelo y creadora argentina`,
    tagline: "Universo visual propio en moda, lifestyle y contenido digital desde Buenos Aires.",
  },
  // México — el mercado LATAM con mayor TP del audit Ahrefs.
  MX: {
    country: "MX",
    locale: "es-MX",
    title: `${BRAND_NAME} | Influencer Mexicanas referente · Modelo Latina`,
    description: `${BRAND_NAME} — referente para influencer mexicanas y creadoras latinas. Modelo argentina con comunidad activa en México.`,
    h1: `${BRAND_NAME}: modelo y creadora con audiencia en México`,
    tagline: "Moda, lifestyle y comunidad exclusiva — para audiencias en México y toda LATAM.",
  },
  // Colombia.
  CO: {
    country: "CO",
    locale: "es-CO",
    title: `${BRAND_NAME} | Modelo Colombiana referente · Influencer Latina`,
    description: `${BRAND_NAME} — referente para modelo colombiana e influencer latina. Creadora argentina con audiencia activa en Colombia.`,
    h1: `${BRAND_NAME}: modelo y creadora con audiencia en Colombia`,
    tagline: "Moda y lifestyle — comunidad activa en Colombia y toda LATAM.",
  },
  // Perú.
  PE: {
    country: "PE",
    locale: "es-PE",
    title: `${BRAND_NAME} | Modelo Peruana referente · Influencer Latina`,
    description: `${BRAND_NAME} — referente para modelo peruana e influencer latina. Creadora argentina con audiencia activa en Perú.`,
    h1: `${BRAND_NAME}: modelo y creadora con audiencia en Perú`,
    tagline: "Moda y lifestyle — comunidad activa en Perú y toda LATAM.",
  },
  // Chile.
  CL: {
    country: "CL",
    locale: "es-CL",
    title: `${BRAND_NAME} | Modelo Chilena referente · Influencer Latina`,
    description: `${BRAND_NAME} — referente para influencer chilena y modelo latina. Creadora argentina con audiencia activa en Chile.`,
    h1: `${BRAND_NAME}: modelo y creadora con audiencia en Chile`,
    tagline: "Moda y lifestyle — comunidad activa en Chile y toda LATAM.",
  },
  // United States — English content (audiencia Hispanic + general).
  US: {
    country: "US",
    locale: "en-US",
    title: `${BRAND_NAME} | Latina Model and Content Creator from Argentina`,
    description: `${BRAND_NAME} — Argentinian model and content creator with exclusive community. Latin American aesthetic, English subtitles, active US Hispanic audience.`,
    h1: `${BRAND_NAME}: Argentinian model and content creator`,
    tagline: "Premium visual content, lifestyle and fashion — with active US audience.",
  },
  // Spain.
  ES: {
    country: "ES",
    locale: "es-ES",
    title: `${BRAND_NAME} | Modelo Argentina · Creadora de Contenido en España`,
    description: `${BRAND_NAME} — modelo argentina y creadora de contenido con audiencia en España. Moda, lifestyle, comunidad exclusiva.`,
    h1: `${BRAND_NAME}: modelo y creadora con audiencia en España`,
    tagline: "Moda y lifestyle — comunidad activa en España y toda LATAM.",
  },
  // Brazil — Portuguese.
  BR: {
    country: "BR",
    locale: "pt-BR",
    title: `${BRAND_NAME} | Modelo Argentina · Influencer Latina no Brasil`,
    description: `${BRAND_NAME} — modelo argentina e criadora de conteúdo com audiência ativa no Brasil. Moda, lifestyle e comunidade VIP.`,
    h1: `${BRAND_NAME}: modelo e criadora com audiência no Brasil`,
    tagline: "Moda e lifestyle — comunidade ativa no Brasil e em toda LATAM.",
  },
  // Default — visitors fuera del set (EU/Asia/UNKNOWN).
  DEFAULT: {
    country: "INTL",
    locale: "es",
    title: `${BRAND_NAME} | Influencer y Modelo Argentina · Content Creator`,
    description: `${BRAND_NAME} — influencer argentina, modelo y creadora de contenido. Moda, lifestyle y comunidad exclusiva — para toda LATAM y el mundo.`,
    h1: `${BRAND_NAME}: modelo y creadora argentina`,
    tagline: "Moda, lifestyle y contenido digital — para audiencias en LATAM y el mundo.",
  },
};

/**
 * Devuelve el variant para un código de país; si no matchea, cae al default.
 */
export function getHomeVariantForCountry(countryCode: string | undefined): HomeVariant {
  if (!countryCode) return HOME_COUNTRY_VARIANTS.DEFAULT;
  const code = countryCode.toUpperCase();
  return HOME_COUNTRY_VARIANTS[code] ?? HOME_COUNTRY_VARIANTS.DEFAULT;
}
